import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationsContext';
import { Container, Spinner, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { categoryService } from '../../services';
import { Category } from '../../types';
import CategoryForm from '../CategoryForm';
import DeleteModal from '../Common/DeleteModal';
import { toast } from 'react-toastify';
import { getDashboardNotificationPath } from '../../utils/notificationUtils';
import { BRAND_ADMIN_FULL_NAME, BRAND_ADMIN_LOGO_MARK } from '../../constants/brand';
import '../../assets/scss/themes.scss';

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set(['content']));
  const [categoryModal, setCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; id: string; name: string }>({
    show: false,
    id: '',
    name: '',
  });

  const menuItems: Array<{
    id: string;
    label: string;
    icon: string;
    link: string;
    hasSubmenu?: boolean;
  }> = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'ri-dashboard-3-line',
      link: '/dashboard',
    },
    {
      id: 'content',
      label: 'Content Management',
      icon: 'ri-file-text-line',
      link: '/dashboard/content',
      hasSubmenu: true,
    },
    {
      id: 'oracle-cards',
      label: 'Oracle Cards',
      icon: 'ri-vip-crown-line',
      link: '/dashboard/oracle-cards',
    },
    {
      id: 'oracle-reading',
      label: 'Deeper Oracle Reading',
      icon: 'ri-customer-service-2-line',
      link: '/dashboard/oracle-reading',
    },
    {
      id: 'home-images',
      label: 'Home Images',
      icon: 'ri-image-line',
      link: '/dashboard/home-images',
    },
    {
      id: 'home-section-order',
      label: 'Home section order',
      icon: 'ri-layout-row-line',
      link: '/dashboard/home-section-order',
    },
    {
      id: 'monthly-theme',
      label: 'Monthly Theme',
      icon: 'ri-calendar-2-line',
      link: '/dashboard/monthly-theme',
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: 'ri-notification-3-line',
      link: '/dashboard/notifications',
    },
    {
      id: 'users',
      label: 'User Management',
      icon: 'ri-group-line',
      link: '/dashboard/users',
    },
    {
      id: 'invoices',
      label: 'Invoices & Subscriptions',
      icon: 'ri-bill-line',
      link: '/dashboard/invoices',
    },
    {
      id: 'diary-tags',
      label: 'Diary Tags',
      icon: 'ri-price-tag-3-line',
      link: '/dashboard/diary/tags',
    },
    {
      id: 'diary-event-types',
      label: 'Event Types',
      icon: 'ri-shapes-line',
      link: '/dashboard/diary/event-types',
    },
    {
      id: 'diary-calendar',
      label: 'Calendar Events',
      icon: 'ri-calendar-event-line',
      link: '/dashboard/diary/calendar-events',
    },
  ];

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoadingCategories(true);
    try {
      // Fetch all categories with high limit so sidebar gets all parents + subcategories (no pagination issue)
      const response = await categoryService.getAll(1, 500);
      const data = Array.isArray(response) ? response : response.data || [];
      setCategories(data);
    } catch (err: any) {
      console.error('Failed to load categories');
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Old structure: filter to parents only (no parentId or parentId is null)
  const parentCategories = useMemo(() => {
    return categories.filter(cat => {
      if (!cat.parentId) return true;
      if (typeof cat.parentId === 'object' && cat.parentId !== null) return false;
      return false;
    });
  }, [categories]);

  const getSubcategories = (parentId: string) => {
    return categories.filter(cat => {
      if (!cat.parentId) return false;
      if (typeof cat.parentId === 'object' && cat.parentId !== null) {
        return (cat.parentId as any)._id === parentId;
      }
      return cat.parentId === parentId;
    });
  };

  const toggleMenu = (menuId: string) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(menuId)) {
      newExpanded.delete(menuId);
    } else {
      newExpanded.add(menuId);
    }
    setExpandedMenus(newExpanded);
  };

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleCategoryClick = (category: Category) => {
    const isParent = !category.parentId || (typeof category.parentId === 'object' && !category.parentId);
    navigate(`/dashboard/content/${category._id}`);
    if (isParent) {
      toggleCategory(category._id);
    }
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryModal(true);
  };

  const handleEditCategory = (category: Category, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingCategory(category);
    setCategoryModal(true);
  };

  const handleAddSubcategory = (parentCategory: Category, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingCategory({ parentId: parentCategory._id } as Category);
    setCategoryModal(true);
  };

  const handleCategorySaved = () => {
    setCategoryModal(false);
    setEditingCategory(null);
    loadCategories();
    toast.success(editingCategory?._id ? 'Category updated successfully' : 'Category created successfully');
  };

  const handleDeleteCategory = (category: Category, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteModal({
      show: true,
      id: category._id,
      name: category.name,
    });
  };

  const confirmDelete = async () => {
    try {
      await categoryService.delete(deleteModal.id);
      toast.success('Category deleted successfully');
      loadCategories();
    } catch (err: any) {
      toast.error('Failed to delete category');
    } finally {
      setDeleteModal({ show: false, id: '', name: '' });
    }
  };

  return (
    <div className="app-menu navbar-menu">
      <div className="navbar-brand-box">
        <Link to="/dashboard" className="logo logo-dark">
          <span className="logo-sm">
            <h4 className="text-white mb-0">{BRAND_ADMIN_LOGO_MARK}</h4>
          </span>
          <span className="logo-lg">
            <h4 className="text-white mb-0">{BRAND_ADMIN_FULL_NAME}</h4>
          </span>
        </Link>
      </div>

      <div id="scrollbar">
        <div className="container-fluid">
          <ul className="navbar-nav" id="navbar-nav">
            <li className="menu-title"><span>Menu</span></li>
            {menuItems.map((item) => (
              <li className="nav-item" key={item.id}>
                {item.hasSubmenu ? (
                  <>
                    <div className="content-menu-container">
                      <Link 
                        to={item.link} 
                        className="nav-link menu-link content-menu-item"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          textDecoration: 'none',
                          flex: 1
                        }}
                      >
                        <i className={item.icon}></i>
                        <span className="flex-grow-1">{item.label}</span>
                      </Link>
                      <button
                        className="menu-arrow-btn"
                        onClick={() => toggleMenu(item.id)}
                        title={expandedMenus.has(item.id) ? 'Collapse' : 'Expand'}
                      >
                        <i className={`ri-arrow-${expandedMenus.has(item.id) ? 'down' : 'right'}-s-line menu-arrow`}></i>
                      </button>
                    </div>
                    
                    {expandedMenus.has(item.id) && (
                      <ul className="nav nav-sm flex-column" style={{ paddingLeft: '0', marginTop: '8px' }}>            {loadingCategories ? (
              <li className="nav-item">
                <div className="text-center py-3">
                  <Spinner size="sm" color="light" />
                </div>
              </li>
            ) : (
              <>
                {parentCategories.map((parent) => {
                  const subcategories = getSubcategories(parent._id);
                  const isExpanded = expandedCategories.has(parent._id);

                  return (
                    <li className="nav-item" key={parent._id}>
                      <div className="category-container">
                        <button
                          className="nav-link menu-link category-link"
                          onClick={() => handleCategoryClick(parent)}
                        >
                          <i className="ri-folder-line text-warning fs-5 me-2 category-icon"></i>
                          <span className="category-name-wrapper">
                            <span className="category-name-text">{parent.name}</span>
                            {subcategories.length > 0 && (
                              <>
                                <span className="badge bg-secondary-subtle text-secondary rounded-pill ms-2 category-badge">
                                  {subcategories.length}
                                </span>
                                <i className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line ms-2 category-arrow`}></i>
                              </>
                            )}
                          </span>
                        </button>
                        
                        <div className="category-actions-slide">
                          <button
                            className="action-btn-slide btn-edit"
                            onClick={(e) => handleEditCategory(parent, e)}
                            title="Edit Category"
                          >
                            <i className="ri-edit-2-line"></i>
                          </button>
                          <button
                            className="action-btn-slide btn-add"
                            onClick={(e) => handleAddSubcategory(parent, e)}
                            title="Add Subcategory"
                          >
                            <i className="ri-add-line"></i>
                          </button>
                          <button
                            className="action-btn-slide btn-delete"
                            onClick={(e) => handleDeleteCategory(parent, e)}
                            title="Delete Category"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>
                      
                      {isExpanded && subcategories.length > 0 && (
                        <ul className="submenu">
                          {subcategories.map((sub) => (
                            <li key={sub._id}>
                              <div className="subcategory-container">
                                <button
                                  className="nav-link menu-link subcategory-link"
                                  onClick={() => handleCategoryClick(sub)}
                                >
                                  <i className="ri-folder-open-line text-info me-2 category-icon"></i>
                                  <span className="category-name-wrapper">
                                    <span className="category-name-text">{sub.name}</span>
                                  </span>
                                </button>
                                
                                <div className="category-actions-slide">
                                  <button
                                    className="action-btn-slide btn-edit"
                                    onClick={(e) => handleEditCategory(sub, e)}
                                    title="Edit Subcategory"
                                  >
                                    <i className="ri-edit-2-line"></i>
                                  </button>
                                  <button
                                    className="action-btn-slide btn-delete"
                                    onClick={(e) => handleDeleteCategory(sub, e)}
                                    title="Delete Subcategory"
                                  >
                                    <i className="ri-delete-bin-line"></i>
                                  </button>
                                </div>
                              </div>
                            </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}

                  {parentCategories.length === 0 && (
                    <li className="nav-item">
                      <div className="text-center text-muted py-3 px-2">
                        <i className="ri-folder-open-line fs-4"></i>
                        <p className="mb-0 mt-2 small">No categories yet</p>
                      </div>
                    </li>
                  )}
                </>
              )}
                      </ul>
                    )}
                  </>
                ) : (
                  <Link to={item.link} className="nav-link menu-link">
                    <i className={item.icon}></i>
                    <span>{item.label}</span>
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
      
      <style>{`
        /* Category Container */
        .category-container,
        .subcategory-container {
          position: relative;
          overflow: hidden;
          margin: 2px 8px;
          border-radius: 6px;
          max-width: 100%;
        }
        
        /* Category Links */
        .category-link,
        .subcategory-link {
          display: flex;
          align-items: flex-start;
          padding: 10px 16px !important;
          margin: 0 !important;
          border-radius: 6px;
          border: none;
          background: transparent;
          color: rgba(255, 255, 255, 0.9);
          text-align: left;
          cursor: pointer;
          transition: all 0.3s ease;
          width: 100%;
          position: relative;
          min-width: 0;
        }
        
        .category-link:hover,
        .subcategory-link:hover {
          background: rgba(255, 255, 255, 0.08);
        }
        
        .category-icon {
          transition: transform 0.3s ease;
          flex-shrink: 0;
          margin-top: 2px;
        }
        
        /* Name Wrapper with Slide Animation */
        .category-name-wrapper {
          display: flex;
          align-items: center;
          flex: 1;
          min-width: 0;
          transition: transform 0.3s ease, opacity 0.3s ease;
        }
        
        .category-container:hover .category-name-wrapper,
        .subcategory-container:hover .category-name-wrapper {
          transform: translateX(-100%);
          opacity: 0;
        }
        
        .category-name-text {
          word-wrap: break-word;
          word-break: break-word;
          overflow-wrap: break-word;
          flex: 1;
          min-width: 0;
          line-height: 1.4;
        }
        
        .category-badge,
        .category-arrow {
          transition: opacity 0.3s ease;
          flex-shrink: 0;
          align-self: flex-start;
          margin-top: 2px;
        }
        
        .category-container:hover .category-badge,
        .category-container:hover .category-arrow,
        .subcategory-container:hover .category-badge {
          opacity: 0;
        }
        
        /* Content Menu Item */
        .content-menu-container {
          display: flex;
          align-items: center;
          width: 100%;
          position: relative;
        }
        
        .content-menu-item {
          flex: 1;
          padding: 0.75rem 1.5rem;
          transition: all 0.2s ease;
        }
        
        .content-menu-item:hover {
          background-color: rgba(255, 255, 255, 0.05);
          text-decoration: none;
        }
        
        .menu-arrow-btn {
          width: 40px;
          height: 100%;
          padding: 0;
          margin: 0;
          border: none;
          background: transparent;
          color: rgba(255, 255, 255, 0.7);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          position: relative;
        }
        
        .menu-arrow-btn::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 1px;
          height: 24px;
          background: rgba(255, 255, 255, 0.1);
        }
        
        .menu-arrow-btn:hover {
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
        }
        
        .menu-arrow-btn:hover .menu-arrow {
          transform: scale(1.2);
        }
        
        .menu-arrow {
          flex-shrink: 0;
          transition: transform 0.3s ease;
          font-size: 18px;
        }
        
        /* Action Buttons Slide In */
        .category-actions-slide {
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateX(100%) translateY(-50%);
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 0 12px;
          opacity: 0;
          transition: all 0.3s ease;
          pointer-events: none;
          background: linear-gradient(to right, transparent, #2a3042 20%, #2a3042);
        }
        
        .category-container:hover .category-actions-slide,
        .subcategory-container:hover .category-actions-slide {
          transform: translateX(0) translateY(-50%);
          opacity: 1;
          pointer-events: auto;
        }
        
        .action-btn-slide {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          padding: 0;
          border: none;
          border-radius: 6px;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .btn-edit {
          background: rgba(41, 156, 219, 0.15);
          color: #299cdb;
        }
        
        .btn-edit:hover {
          background: #299cdb;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(41, 156, 219, 0.3);
        }
        
        .btn-add {
          background: rgba(10, 179, 156, 0.15);
          color: #0ab39c;
        }
        
        .btn-add:hover {
          background: #0ab39c;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(10, 179, 156, 0.3);
        }
        
        .btn-delete {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }
        
        .btn-delete:hover {
          background: #ef4444;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3);
        }
        
        .action-btn-slide i {
          font-size: 16px;
        }
        
        /* Submenu */
        .submenu {
          list-style: none;
          padding-left: 30px;
          margin: 4px 0;
        }
        
        .submenu li {
          margin: 2px 0;
          max-width: 100%;
        }
      `}</style>
      
      {/* Category Modal */}
      <Modal isOpen={categoryModal} toggle={() => setCategoryModal(false)} size="lg">
        <ModalHeader toggle={() => setCategoryModal(false)}>
          {editingCategory?._id ? 'Edit Category' : (editingCategory?.parentId ? 'Add Subcategory' : 'Add Parent Category')}
        </ModalHeader>
        <ModalBody>
          <CategoryForm
            category={editingCategory}
            onSave={handleCategorySaved}
            onCancel={() => setCategoryModal(false)}
          />
        </ModalBody>
      </Modal>
      
      {/* Delete Modal */}
      <DeleteModal
        show={deleteModal.show}
        onDeleteClick={confirmDelete}
        onCloseClick={() => setDeleteModal({ show: false, id: '', name: '' })}
      />
    </div>
  );
};

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { unreadCount, notifications, socketConnected, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notifDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (notifDropdownRef.current && !notifDropdownRef.current.contains(event.target as Node)) {
        setNotifDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setDropdownOpen(false);
    logout();
    navigate('/login');
  };

  const recentNotifications = (notifications || []).slice(0, 8);

  return (
    <header id="page-topbar">
      <div className="layout-width">
        <div className="navbar-header">
          <div className="d-flex">
            <div className="navbar-brand-box horizontal-logo">
              <Link to="/dashboard" className="logo logo-dark">
                <span className="logo-sm">
                  <h5 className="mb-0">{BRAND_ADMIN_LOGO_MARK}</h5>
                </span>
                <span className="logo-lg">
                  <h5 className="mb-0">{BRAND_ADMIN_FULL_NAME}</h5>
                </span>
              </Link>
            </div>
          </div>

          <div className="d-flex align-items-center">
            {/* Notifications */}
            <div className={`dropdown ms-sm-2 header-item topbar-notif ${notifDropdownOpen ? 'show' : ''}`} ref={notifDropdownRef}>
              <button
                type="button"
                className="btn btn-ghost position-relative"
                onClick={() => setNotifDropdownOpen(!notifDropdownOpen)}
                aria-label="Notifications"
                title={socketConnected ? 'Notifications (live)' : 'Notifications'}
              >
                <i className="ri-notification-3-line fs-18"></i>
                {socketConnected && (
                  <span className="position-absolute bottom-0 end-0 rounded-circle bg-success border border-2 border-body" style={{ width: 8, height: 8 }} title="Live" />
                )}
                {unreadCount > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger notif-badge">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              <div className={`dropdown-menu dropdown-menu-end topbar-notif-dropdown ${notifDropdownOpen ? 'show' : ''}`} style={notifDropdownOpen ? { display: 'block' } : undefined}>
                <div className="notif-dropdown-header">
                  <span className="notif-dropdown-title">Notifications</span>
                  {unreadCount > 0 && (
                    <button type="button" className="btn-mark-all" onClick={() => { markAllAsRead(); setNotifDropdownOpen(false); }}>
                      Mark all read
                    </button>
                  )}
                </div>
                <div className="dropdown-divider"></div>
                {recentNotifications.length === 0 ? (
                  <div className="notif-empty">
                    <i className="ri-notification-off-line fs-3 text-muted"></i>
                    <p className="mb-0 mt-2 text-muted small">No notifications yet</p>
                  </div>
                ) : (
                  <div className="notif-list">
                    {recentNotifications.map((n: any) => (
                      <div
                        key={n._id}
                        className={`notif-item ${!n.read ? 'unread' : ''}`}
                        onClick={() => {
                          if (!n.read) markAsRead(n._id);
                          setNotifDropdownOpen(false);
                          const path = getDashboardNotificationPath(
                            n.actionUrl,
                            n.data as { contentId?: string; orderId?: string; userId?: string },
                            n.type,
                          );
                          if (path) navigate(path);
                        }}
                      >
                        <div className="notif-item-inner">
                          <span className="notif-item-title">{n.title}</span>
                          {!n.read && <span className="notif-item-badge">New</span>}
                        </div>
                        <p className="notif-item-message">{n.message}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="dropdown-divider"></div>
                <button
                  type="button"
                  className="notif-view-all"
                  onClick={() => { setNotifDropdownOpen(false); navigate('/dashboard/notifications'); }}
                >
                  <i className="ri-arrow-right-line me-1"></i> View all notifications
                </button>
              </div>
            </div>

            <div className={`dropdown ms-sm-3 header-item topbar-user ${dropdownOpen ? 'show' : ''}`} ref={dropdownRef}>
              <button
                type="button"
                className="btn btn-ghost"
                id="page-header-user-dropdown"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-haspopup="true"
                aria-expanded={dropdownOpen}
              >
                <span className="d-flex align-items-center">
                  <span className="avatar-xs rounded-circle me-2 overflow-hidden d-flex align-items-center justify-content-center" style={{ minWidth: 32, minHeight: 32 }}>
                    {user?.avatar ? (
                      <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span className="avatar-title bg-primary-subtle text-primary" style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{user?.name?.charAt(0) || 'A'}</span>
                    )}
                  </span>
                  <span className="text-start ms-xl-2">
                    <span className="d-none d-xl-inline-block ms-1 fw-medium user-name-text">
                      {user?.name || 'Admin'}
                    </span>
                    <span className="d-none d-xl-block ms-1 fs-12 text-muted user-name-sub-text">
                      {user?.role || 'Administrator'}
                    </span>
                  </span>
                  <i className={`ri-arrow-down-s-line ms-2 dropdown-arrow ${dropdownOpen ? 'rotate' : ''}`}></i>
                </span>
              </button>
              <div className={`dropdown-menu dropdown-menu-end topbar-dropdown ${dropdownOpen ? 'show' : ''}`} style={dropdownOpen ? { display: 'block' } : undefined}>
                <div className="dropdown-header-custom">
                  <span className="dropdown-header-avatar">
                    {user?.avatar ? (
                      <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                    ) : (
                      user?.name?.charAt(0) || 'A'
                    )}
                  </span>
                  <div>
                    <div className="dropdown-header-name">{user?.name || 'Admin'}</div>
                    <div className="dropdown-header-email">{user?.email || user?.role || 'Administrator'}</div>
                  </div>
                </div>
                <div className="dropdown-divider"></div>
                <button type="button" className="dropdown-item" onClick={() => { setDropdownOpen(false); navigate('/dashboard/profile'); }}>
                  <i className="ri-user-line"></i>
                  <span>Profile</span>
                </button>
                <button type="button" className="dropdown-item dropdown-item-logout" onClick={handleLogout}>
                  <i className="ri-logout-box-r-line"></i>
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

const DashboardLayout: React.FC = () => {
  return (
    <div id="layout-wrapper">
      <Header />
      <Sidebar />
      <div className="main-content">
        <div className="page-content">
          <Container fluid>
            <Outlet />
          </Container>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
